import * as React from 'react';

import { cn } from '@repo/ui/lib/utils';
import { getLimitStatus, type LimitThresholds } from '@repo/ui/lib/limit-status';

/**
 * Fill color for a limit progress bar. `neutral` is a 4th, non-semantic
 * state for bars that don't represent a limit/threshold at all — a plain
 * informational progress indicator (e.g. "% deployed") — kept alongside the
 * 3-state `within-limit`/`near-limit`/`exceeded` semantic used by actual
 * limit-tracking bars (Risk limits, Allocation drift, Mandate compliance).
 */
type LimitProgressStatus = 'within-limit' | 'near-limit' | 'exceeded' | 'neutral';

interface LimitProgressBarProps {
  /** Fill position, 0-100. */
  pct: number;
  label?: React.ReactNode;
  /**
   * Explicit status override. If omitted, the status is derived from `pct`
   * and `thresholds` via `getLimitStatus`; if `thresholds` is also omitted,
   * the bar renders as `neutral`.
   */
  status?: LimitProgressStatus;
  thresholds?: LimitThresholds;
  className?: string;
}

const fillClassName: Record<LimitProgressStatus, string> = {
  'within-limit': 'bg-rowa-status-positive',
  'near-limit': 'bg-rowa-status-warning',
  exceeded: 'bg-rowa-status-error',
  neutral: 'bg-rowa-text-tertiary/35'
};

/**
 * Shared limit/progress bar for values tracked against a threshold — the
 * fill color communicates whether the value is within limit (brand green),
 * near a limit (warning/orange), or below minimum / over limit (error/red).
 * Used by Risk limits, Allocation drift, and Mandate compliance, and by
 * `MetricTile` for plain informational progress (via `status="neutral"`).
 */
function LimitProgressBar({ pct, label, status, thresholds, className }: LimitProgressBarProps) {
  const resolvedStatus: LimitProgressStatus =
    status ?? (thresholds ? getLimitStatus(pct, thresholds) : 'neutral');
  const clampedPct = Math.min(100, Math.max(0, pct));

  return (
    <div data-slot="limit-progress-bar" className={cn('w-full', className)}>
      <div className="bg-rowa-border-subtle h-1.5 overflow-hidden rounded-full">
        <div
          data-status={resolvedStatus}
          className={cn('h-full rounded-full transition-[width]', fillClassName[resolvedStatus])}
          style={{ width: `${clampedPct}%` }}
        />
      </div>
      {label && <div className="text-rowa-label-sm text-rowa-text-tertiary mt-2">{label}</div>}
    </div>
  );
}

export { LimitProgressBar };
export type { LimitProgressBarProps, LimitProgressStatus };
